import Image from "next/image"
import { Component, ReactNode } from "react"
import { Trans } from "react-i18next"
import { ReturnToWorkspaceButton } from "themes/default-tailwind/buttons/ReturnToWorkspaceButton"

type Props = {
    children: ReactNode
}

type State = {
    hasError: boolean
}

/*
 *  A component that uses the getDerivedStateFromError to catch errors its children produces and renders a fallback UI
 *  Written as a class component specifically to gain access to getDerivedStateFromError
 *  https://reactjs.org/docs/error-boundaries.html#:~:text=Only%20class%20components%20can%20be,catch%20an%20error%20within%20itself.
 */
class PageErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        // Resets state on page switch (from page with error to other page)
        if (prevState.hasError) this.setState({ hasError: false })
    }

    static getDerivedStateFromError(error: Error) {
        console.error(error)
        return { hasError: true }
    }

    render() {
        if (!this.state.hasError) return this.props.children
        return (
            <div className="nova-center-screen-container">
                <div className="nova-error-container flex flex-col items-center">
                    <Image
                        src="/images/logos/nova-logo-with-title.svg"
                        width={150}
                        height={0}
                        alt="Nova"
                        priority={true}
                    />
                    <h1 className="nova-error-header">
                        <Trans i18nKey="Something went wrong." />
                    </h1>
                    <h5 className="max-w-[34rem] mb-4 text-gray-100">
                        <Trans i18nKey="We've been notified of the problem and are resolving this." />
                    </h5>
                    <h5 className="mb-5 text-gray-100" style={{ width: "270px" }}>
                        <Trans i18nKey="If you require immediate assistance, please email us at " />
                        <a href="mailto:support@mapnova.com" className="text-blue-500">
                            <Trans i18nKey="support@mapnova.com" />
                        </a>
                    </h5>

                    <ReturnToWorkspaceButton />
                </div>
            </div>
        )
    }
}

export default PageErrorBoundary
